<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/External_Components/A_Wrapper_with_a_Button';
    let nextLink= '/Examples/External_Components/A_Sorted_Table';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="replacing-a-part-list-1"
  bind:intersecting={visibleById["replacing-a-part-list-1"]}
  onIntersectChange={(visible) => setVisible("replacing-a-part-list-1", visible)}
>Replacing a Part List</SectionComponent>
<p>In this part of the extended example, you’ll learn how to replace a <strong>part list</strong> in your application by creating a custom Svelte component, <code>StaffAccordion.svelte</code>, to display and manage a list of teachers within an accordion.</p>
<SectionComponent tag="h2" 
  id="step-1-create-the-svelte-component-2"
  bind:intersecting={visibleById["step-1-create-the-svelte-component-2"]}
  onIntersectChange={(visible) => setVisible("step-1-create-the-svelte-component-2", visible)}
>Step 1: Create the Svelte Component</SectionComponent>
<p>We’ll create the <code>StaffAccordion.svelte</code> component to replace the default projection of the <code>teachers</code> property in the <code>Staff</code> model unit.</p>
<h3 id="the-script-section-1">The Script Section</h3>
<p>Begin by defining the component’s parameters and the necessary state management functions:</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase4/src/external/StaffAccordion.svelte#L9-L55

// This property is a parts list, therefore the external box to use is an PartListReplacerBox.
// Props
let &#123; editor, box &#125;: FreComponentProps&lt;PartListReplacerBox&gt; = $props();

let panelOpen: boolean[] = $state([]);      // List of booleans to indicate which panel is open (true) and closed (false).
let multiplePar: boolean = $state(false);   // Indicates whether multiple panels may be open at the same time.

let ch = $state([...box.children])
/*
    Sets all panels in the state 'closed',
    and sets the length of 'panelOpen'.
 */
function initialize() &#123;
    let param: string | undefined = box.findParam("multi");
    if (param === "multiple") &#123;
        multiplePar = true;
    &#125;
    panelOpen = []
    for (let i = 0; i &lt; box.children.length; i++) &#123;
        // this also sets the length of panelOpen!
        panelOpen[i] = false;
        box.children[i].isVisible = false; // the child boxes are not currently shown
    &#125;
&#125;

// The following three functions need to be included for the editor to function properly.
// Please, set the focus to the first editable/selectable element in this component.
async function setFocus(): Promise&lt;void&gt; &#123;
    for( let i=0; i &lt; box.children.length; i++) &#123;
        if (panelOpen[i]) &#123;
            box.children[i].setFocus();
        &#125;
    &#125;
&#125;
const refresh = (why?: string): void =&gt; &#123;
    console.log("REFRESH ACCORDION")
    // do whatever needs to be done to refresh the elements that show information from the model
    untrack( () =&gt; initialize() );
&#125;;
const addPerson = () =&gt; &#123;
    // Note that you need to put any changes to the actual model in a 'AST.change or AST.changeNamed',
    // because all elements in the model are reactive using mobx.
    AST.change(() =&gt; &#123;
        let newPerson: Person = Person.create(&#123;&#125;);
        box.getPropertyValue().push(newPerson);
    &#125;);
&#125;</code></pre>
<p>We implement <code>setFocus</code> so that when focus is programmatically set to the list, it forwards focus to the first <strong>open</strong> panel’s content.<br>
<code>initialize</code> reads the <code>multi</code> parameter (from the <code>.edit</code> file) and initializes <code>panelOpen</code>.</p>
<p>We also provide <code>addPerson</code> and <code>removePerson</code> to mutate the list. Because the AST is reactive via <strong>MobX</strong>, changes must be wrapped in <code>AST.change</code> (or <code>AST.changeNamed</code>).</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase4/src/external/StaffAccordion.svelte#L57-L72

const removePerson = (index: number) =&gt; &#123;
    // Note that you need to put any changes to the actual model in a 'AST.change' or
    // 'AST.changeNamed', because all elements in the AST model are reactive using mobx.
    AST.change(() =&gt; &#123;
        box.getPropertyValue().splice(index, 1);
    &#125;);
&#125;
// Run the initialization
initialize();

$effect(() =&gt; &#123;
    // $inspect.trace(`accordion for $effect $&#123;box.children.length&#125; id $&#123;box.id&#125;`)
    // console.log(`========== $effect $&#123;box.children.length&#125; id $&#123;box.id&#125;` )
    box.setFocus = setFocus;
    box.refreshComponent = refresh;
    // Needed to get an effect</code></pre>
<h3 id="the-html-section-2">The HTML Section</h3>
<p>The HTML consists of a wrapper <code>div</code> with an <code>Accordion</code> and a button to <strong>add</strong> items.<br>
Inside the <code>Accordion</code>, we iterate over <code>box.children</code>, creating a <code>Panel</code> per child.<br>
For each panel header, we use AST info from <code>childBox.node</code>:</p>
<ul>
<li><code>childBox.node.freLanguageConcept()</code> → the meta concept name (e.g., <code>Person</code>)</li>
<li><code>childBox.node.freId()</code> → a stable id</li>
<li>For ad-hoc access to properties without casting, we use <strong>typed index access</strong>: <code>childBox.node["name"]</code>.</li>
</ul>
<p>The panel content renders the native Freon component for the child via <code>RenderComponent</code>, and includes a <strong>remove</strong> button.</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase4/src/external/StaffAccordion.svelte#L78-L96

    &#125;);

    const colorCls: string = 'text-light-base-50 dark:text-dark-base-900 ';
    const buttonCls: string =
      'bg-light-base-600 					dark:bg-dark-base-200 ' +
      'hover:bg-light-base-900 		dark:hover:bg-dark-base-50 ' +
      'border-light-base-100 			dark:border-dark-base-800 ';
    const iconCls: string = 'ms-0 inline h-6 w-6';
&lt;/script&gt;

&lt;div style="display: flex; align-items: flex-end;"&gt;
    &lt;Accordion multiple=&#123;multiplePar&#125;&gt;
        &#123;#each ch as childBox, index&#125;
            &lt;AccordionItem bind:open=&#123;panelOpen[index]&#125;&gt;
                &#123;#snippet header()&#125;
                    &#123;childBox.node.freLanguageConcept()&#125; &#123;childBox.node.freId()&#125;
                &#123;/snippet&#125;

                    &lt;div style="display: flex; align-items: flex-end;"&gt;</code></pre>
<h3 id="the-complete-component-3">The Complete Component</h3>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase4/src/external/StaffAccordion.svelte

&lt;script lang="ts"&gt;
    import &#123; AccordionItem, Accordion, Button &#125; from 'flowbite-svelte';
    import &#123; AST, PartListReplacerBox &#125; from "@freon4dsl/core";
    import &#123; type FreComponentProps, RenderComponent &#125; from "@freon4dsl/core-svelte";
    import &#123; Person &#125; from "../freon/index.js";
    import &#123; untrack &#125; from "svelte"
    import &#123; UserAddOutline, UserRemoveOutline &#125; from 'flowbite-svelte-icons';
    // This component replaces the component for "teachers: Person[];" from model unit "Staff".
    // This property is a parts list, therefore the external box to use is an PartListReplacerBox.
    // Props
    let &#123; editor, box &#125;: FreComponentProps&lt;PartListReplacerBox&gt; = $props();

    let panelOpen: boolean[] = $state([]);      // List of booleans to indicate which panel is open (true) and closed (false).
    let multiplePar: boolean = $state(false);   // Indicates whether multiple panels may be open at the same time.

    let ch = $state([...box.children])
    /*
        Sets all panels in the state 'closed',
        and sets the length of 'panelOpen'.
     */
    function initialize() &#123;
        let param: string | undefined = box.findParam("multi");
        if (param === "multiple") &#123;
            multiplePar = true;
        &#125;
        panelOpen = []
        for (let i = 0; i &lt; box.children.length; i++) &#123;
            // this also sets the length of panelOpen!
            panelOpen[i] = false;
            box.children[i].isVisible = false; // the child boxes are not currently shown
        &#125;
    &#125;

    // The following three functions need to be included for the editor to function properly.
    // Please, set the focus to the first editable/selectable element in this component.
    async function setFocus(): Promise&lt;void&gt; &#123;
        for( let i=0; i &lt; box.children.length; i++) &#123;
            if (panelOpen[i]) &#123;
                box.children[i].setFocus();
            &#125;
        &#125;
    &#125;
    const refresh = (why?: string): void =&gt; &#123;
        console.log("REFRESH ACCORDION")
        // do whatever needs to be done to refresh the elements that show information from the model
        untrack( () =&gt; initialize() );
    &#125;;
    const addPerson = () =&gt; &#123;
        // Note that you need to put any changes to the actual model in a 'AST.change or AST.changeNamed',
        // because all elements in the model are reactive using mobx.
        AST.change(() =&gt; &#123;
            let newPerson: Person = Person.create(&#123;&#125;);
            box.getPropertyValue().push(newPerson);
        &#125;);
    &#125;

    const removePerson = (index: number) =&gt; &#123;
        // Note that you need to put any changes to the actual model in a 'AST.change' or
        // 'AST.changeNamed', because all elements in the AST model are reactive using mobx.
        AST.change(() =&gt; &#123;
            box.getPropertyValue().splice(index, 1);
        &#125;);
    &#125;
    // Run the initialization
    initialize();

    $effect(() =&gt; &#123;
        // $inspect.trace(`accordion for $effect $&#123;box.children.length&#125; id $&#123;box.id&#125;`)
        // console.log(`========== $effect $&#123;box.children.length&#125; id $&#123;box.id&#125;` )
        box.setFocus = setFocus;
        box.refreshComponent = refresh;
        // Needed to get an effect
        ch = [...box.children]
        // untrack becauise initialize causes a too many  effects error
        untrack( () =&gt; &#123;
            initialize()
        &#125;)        
    &#125;);

    const colorCls: string = 'text-light-base-50 dark:text-dark-base-900 ';
    const buttonCls: string =
      'bg-light-base-600 					dark:bg-dark-base-200 ' +
      'hover:bg-light-base-900 		dark:hover:bg-dark-base-50 ' +
      'border-light-base-100 			dark:border-dark-base-800 ';
    const iconCls: string = 'ms-0 inline h-6 w-6';
&lt;/script&gt;

&lt;div style="display: flex; align-items: flex-end;"&gt;
    &lt;Accordion multiple=&#123;multiplePar&#125;&gt;
        &#123;#each ch as childBox, index&#125;
            &lt;AccordionItem bind:open=&#123;panelOpen[index]&#125;&gt;
                &#123;#snippet header()&#125;
                    &#123;childBox.node.freLanguageConcept()&#125; &#123;childBox.node.freId()&#125;
                &#123;/snippet&#125;

                    &lt;div style="display: flex; align-items: flex-end;"&gt;
                        &lt;RenderComponent box=&#123;childBox&#125; editor=&#123;editor&#125; /&gt;
&lt;!--                        &lt;IconButton class="material-icons" onclick=&#123;() =&gt; removePerson(index)&#125;&gt;remove&lt;/IconButton&gt;--&gt;
                        &lt;Button tabindex=&#123;-1&#125; id="add-button" class="&#123;buttonCls&#125; &#123;colorCls&#125; " name="removePerson" onclick=&#123;() =&gt; removePerson(index)&#125;&gt;
                            &lt;UserRemoveOutline class="&#123;iconCls&#125;" /&gt;
                        &lt;/Button&gt;
                    &lt;/div&gt;
            &lt;/AccordionItem&gt;
        &#123;/each&#125;
    &lt;/Accordion&gt;

&lt;!--    &lt;IconButton class="material-icons" onclick=&#123;() =&gt; addPerson()&#125;&gt;add&lt;/IconButton&gt;--&gt;
    &lt;Button tabindex=&#123;-1&#125; id="add-button" class="&#123;buttonCls&#125; &#123;colorCls&#125; " name="addPerson" onclick=&#123;() =&gt; addPerson()&#125;&gt;
        &lt;UserAddOutline class="&#123;iconCls&#125;" /&gt;
    &lt;/Button&gt;
&lt;/div&gt;
</code></pre>
<SectionComponent tag="h2" 
  id="step-2-include-in-the-projection-3"
  bind:intersecting={visibleById["step-2-include-in-the-projection-3"]}
  onIntersectChange={(visible) => setVisible("step-2-include-in-the-projection-3", visible)}
>Step 2: Include in the Projection</SectionComponent>
<p>Replace the <code>teachers</code> property with the <code>StaffAccordion</code> component and pass the string parameter <code>multi="multiple"</code> to allow multiple panels to be open:</p>
<pre class="language-freon"><code class="language-freon">// CourseSchedule/phase4/defs/externals.edit#L14-L18

Staff &#123;[
Staff in the category: $&#123;self.name&#125;

    $&#123;self.teachers replace=StaffAccordion multi="multiple"&#125;
]&#125;</code></pre>
<SectionComponent tag="h2" 
  id="step-3-do-the-admin-4"
  bind:intersecting={visibleById["step-3-do-the-admin-4"]}
  onIntersectChange={(visible) => setVisible("step-3-do-the-admin-4", visible)}
>Step 3: Do the Admin</SectionComponent>
<p>Add <code>StaffAccordion</code> to the global section and register it as a custom component.</p>
<pre class="language-freon"><code class="language-freon">// CourseSchedule/phase4/defs/main.edit#L3-L9

global &#123;
    external &#123;
        PersonIcon,
        PhoneButton,
        StaffAccordion
    &#125;
&#125;</code></pre>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase4/src/external/externals.ts#L9-L15

export function configureExternals() &#123;
    setCustomComponents([
        &#123; component: PersonIcon, knownAs: "PersonIcon" &#125;,
        &#123; component: PhoneButton, knownAs: "PhoneButton" &#125;,
        &#123; component: StaffAccordion, knownAs: "StaffAccordion" &#125;,
    ]);
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="final-result-5"
  bind:intersecting={visibleById["final-result-5"]}
  onIntersectChange={(visible) => setVisible("final-result-5", visible)}
>Final Result</SectionComponent>
<p>After these steps, your editor displays the staff list in an <strong>accordion</strong>:</p>
<ul>
<li><strong>All panels closed:</strong></li>
</ul>
<Figure
imageName={'examples/CourseSchedule/Screenshot-step4a.png'}
caption={'Staff model unit with Accordion'}
figureNumber={1}
/>
<ul>
<li><strong>A panel open:</strong></li>
</ul>
<Figure
imageName={'examples/CourseSchedule/Screenshot-step4b.png'}
caption={'Accordion with open panel'}
figureNumber={2}
/>
<h3 id="conclusion-4">Conclusion</h3>
<p>That’s it! You’ve replaced the <code>teachers</code> list with a Svelte accordion.<br>
Next, you’ll learn how to reorder AST nodes and display them in a different order.</p>


<PrevNextSection {prevLink} {nextLink} />